import React, { useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import { reverse } from 'named-urls'
import { isNil } from 'lodash'
import { Box, Button } from '@material-ui/core'
import ReCAPTCHA from 'react-google-recaptcha'

import {
  PasswordInputField,
  PasswordValidator,
  RegulationAcceptanceForm,
} from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useClientPortalTheme from 'hooks/useClientPortalTheme'

import constants from 'constants/index'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import { getGoogleRecaptchaToken, isProductionOrHomol } from 'service/env'
import schemas from '../../schemas'

export const Form = ({ defaultValues }) => {
  const { PLATFORM_SLUG_ID } = constants.settings

  const [loading, setLoading] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [recaptcha, setRecaptcha] = useState()

  const recaptchaRef = useRef(null)
  const regulationAcceptanceRef = useRef(null)

  const history = useHistory()
  const snackbar = useSnackbar()
  const { slug } = useParams()

  const { clientPortalTheme } = useClientPortalTheme()
  const classes = useStyles({ clientPortalTheme })()

  const { control, errors, handleSubmit, watch } = useForm({
    validationSchema: schemas.newPassword,
    defaultValues: defaultValues,
  })

  const disableSubmitButton = isProductionOrHomol
    ? !recaptcha || loading
    : false

  const resetRecaptcha = () => {
    if (recaptchaRef.current) recaptchaRef.current.reset()
    setRecaptcha(undefined)
  }

  const hasSlug =
    !isNil(slug) &&
    !constants.settings.DEFAULT_SLUGS_PATHS.includes(slug?.toLowerCase())

  const onSubmit = async (data) => {
    setLoading(true)

    data.resetPasswordToken = defaultValues.resetPasswordToken

    try {
      await service.dponet.users.newPassword({
        ...data,
        ...(hasSlug && { publicPath: slug }),
        recaptcha,
      })

      await regulationAcceptanceRef.current?.accept()

      snackbar.open({
        message:
          'Senha alterada com sucesso. Faça o login com as novas credenciais.',
        variant: 'success',
      })
      history.push(
        reverse(routes.root, {
          slug: slug || PLATFORM_SLUG_ID,
        }),
      )
    } catch (error) {
      if (isProductionOrHomol) resetRecaptcha()
      history.push(
        reverse(routes.root, {
          slug: slug || PLATFORM_SLUG_ID,
        }),
        {
          error:
            error?.response?.data?.error?.message ||
            'Email ou senha inválidos!',
        },
      )
    }
  }

  const handleSubmitForm = (event) => {
    setJustSubmitted(true)

    const regulationsValidation = regulationAcceptanceRef.current?.validate()

    handleSubmit(async (data) => {
      const regulationsAccepted = (await regulationsValidation) ?? true
      if (!regulationsAccepted) return

      await onSubmit(data)
    })(event)
  }

  const handleAcceptedRecaptcha = (recaptchaToken) => {
    setRecaptcha(recaptchaToken)
  }
  const disableRecaptcha = () => setRecaptcha(undefined)

  return (
    <Box>
      <Box mb={3}>
        <PasswordValidator
          passwordInput={watch('password')}
          justSubmitted={justSubmitted}
        />
      </Box>
      <form onSubmit={handleSubmitForm}>
        <Controller
          as={
            <PasswordInputField
              label="Nova senha"
              color="primary"
              variant="outlined"
              error={!!errors.password}
              fullWidth
            />
          }
          control={control}
          name="password"
          mode="onChange"
        />
        <Controller
          as={
            <PasswordInputField
              label="Confirme a senha"
              variant="outlined"
              margin="normal"
              error={!!errors.passwordConfirm}
              helperText={errors?.passwordConfirm?.message}
              fullWidth
            />
          }
          control={control}
          name="passwordConfirm"
          mode="onChange"
        />
        <Box mt={1}>
          <RegulationAcceptanceForm ref={regulationAcceptanceRef} />
        </Box>
        {isProductionOrHomol && (
          <Box
            mt={1}
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              render="explicit"
              sitekey={getGoogleRecaptchaToken()}
              onChange={handleAcceptedRecaptcha}
              onExpired={disableRecaptcha}
            />
          </Box>
        )}
        <Box mt={3}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={disableSubmitButton}
            className={classes.buttonPrimary}
          >
            {loading ? 'Carregando...' : 'Cadastrar nova senha'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default Form
